import previewService from "../services/preview-Service";

/**
 * NMU-451: one place that performs the "ดูตัวอย่างเว็บไซต์" action.
 *
 * Twelve modules had their own copy of this handler, identical apart from the
 * path they open, so the same three faults were reproduced twelve times:
 *
 *  1. the response envelope was misread (fixed in preview-service.js);
 *  2. `window.open` was called inside `setTimeout(…, 500)`, which puts it
 *     outside the click's user-gesture window — browsers block a popup opened
 *     that late, so even once (1) was fixed the tab would silently not appear;
 *  3. it opened a chrome-less named popup
 *     (`"winname", "directories=no,titlebar=no,…"`), which is what popup
 *     blockers are most aggressive about. A plain new tab is what the editors
 *     actually want.
 *
 * Callers now describe *what* to preview and this decides *how*.
 */

/** Origin of the public site that stores and renders previews. */
const previewOrigin = () => (process.env.REACT_APP_PREVIEW_ENDPOINT || "").replace(/\/+$/, "");

/**
 * Saves a draft and opens it on the public site in a new tab.
 *
 * @param {string}   type      PREVIEW_TYPE value — which module the draft is for.
 * @param {object}   [bean]    single-record draft (add/edit dialogs).
 * @param {Array}    [listBean] list-shaped draft (arrangement pages).
 * @param {function} [path]    (token) => path on the public site, defaulting to
 *   the home page. Receives the token because most detail pages fall back to it
 *   when the record has no id yet (an unsaved draft).
 * @returns {Promise<string>} the preview token, for callers that want it.
 * @throws a displayable message — never `undefined` — for notifyUtitls.error.
 */
export const openWebsitePreview = async ({ type, bean, listBean, path }) => {
    const origin = previewOrigin();
    if (!origin) {
        // Misconfigured build rather than a user error; say which knob is missing.
        throw "ยังไม่ได้ตั้งค่าที่อยู่เว็บไซต์สำหรับดูตัวอย่าง (REACT_APP_PREVIEW_ENDPOINT) | Preview site URL is not configured";
    }

    const { token } = await previewService.savePreview({
        preview: { content: type, bean, listBean },
    });

    const target = typeof path === "function" ? path(token) : "/";
    const separator = target.includes("?") ? "&" : "?";
    const url = `${origin}${target.startsWith("/") ? "" : "/"}${target}${separator}token=${encodeURIComponent(token)}`;

    // Opened synchronously after the await so the click that started this is
    // still what the browser attributes the tab to.
    //
    // NMU-481: `noopener` is deliberately NOT in the feature string. Per the HTML
    // spec, window.open() returns null whenever noopener is requested — success or
    // not — so the check below fired on every preview and editors saw the tab open
    // *and* a "your browser blocked it" toast. Clearing `opener` on the handle
    // gives the same protection (the preview cannot reach back into the admin
    // through window.opener) while leaving a return value we can actually test.
    const opened = window.open(url, "_blank");
    if (!opened) {
        throw "เบราว์เซอร์บล็อกการเปิดแท็บใหม่ กรุณาอนุญาต pop-up ของเว็บนี้แล้วลองอีกครั้ง | Your browser blocked the new tab — allow pop-ups for this site and try again";
    }
    opened.opener = null;

    return token;
};

const previewUtils = {
    openWebsitePreview,
};

export default previewUtils;
